// Thin HP arcs over damaged enemies: one merged line draw call per frame.
// Mint → gold → red as HP drops, so focus fire and kill priority read at a
// glance even inside a dense swarm.

import * as THREE from 'three';
import type { Enemy, Vec2 } from '../types.js';
import { wrappedDelta } from '../sim/collision.js';

const SEGMENTS = 16;
const MAX_ENEMIES = 512;
const MAX_VERTS = MAX_ENEMIES * SEGMENTS * 2;

const MINT = new THREE.Color(0x3dffc4);
const GOLD = new THREE.Color(0xffd36b);
const RED = new THREE.Color(0xff4d6b);

export class HpArcs {
  readonly mesh: THREE.LineSegments;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private readonly tmpColor = new THREE.Color();

  constructor() {
    this.positions = new Float32Array(MAX_VERTS * 3);
    this.colors = new Float32Array(MAX_VERTS * 3);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(this.colors, 3));
    this.geometry.setDrawRange(0, 0);
    this.mesh = new THREE.LineSegments(
      this.geometry,
      new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        // Kept subtle: a solid bright ring around every wounded enemy reads
        // as UI chrome, not creature state.
        opacity: 0.45,
        depthWrite: false,
      })
    );
    this.mesh.frustumCulled = false;
  }

  /** Rebuild the arcs for every enemy below full HP, in the camera frame.
   * `time` (sim seconds) drives a soft pulse so the dashes feel organic. */
  update(enemies: Iterable<Enemy>, camAnchor: Vec2, time = 0): void {
    let v = 0;
    for (const e of enemies) {
      const fraction = e.max_hp > 0 ? e.hp / e.max_hp : 0;
      if (fraction >= 0.95 || fraction <= 0) continue;
      if (!e.targetable) continue;

      // Unwrap into the camera's local frame, same as the instanced sprites.
      const d = wrappedDelta(e.pos, camAnchor);
      const cx = camAnchor.x + d.x;
      const cy = camAnchor.y + d.y;
      // Hug the hull: a wide ring reads as a bubble/UI, a tight one as a
      // wound gauge on the creature itself.
      const radius = e.hitbox_radius * 1.35 + 0.25;

      if (fraction > 0.5) {
        this.tmpColor.copy(MINT).lerp(GOLD, (1 - fraction) * 2);
      } else {
        this.tmpColor.copy(RED).lerp(GOLD, fraction * 2);
      }
      // Dim further for barely-hurt enemies; pulse gently on sim time.
      const pulse = 0.55 + (1 - fraction) * 0.3 + Math.sin(time * 6 + e.pool_index) * 0.1;
      this.tmpColor.multiplyScalar(pulse);

      // Clockwise sweep from the top of the circle. Solid but faint: dashing
      // read as UI selection bubbles, a thin dim arc reads as a wound gauge.
      const total = fraction * Math.PI * 2;
      const n = Math.max(1, Math.ceil(fraction * SEGMENTS));
      const da = total / n;
      for (let i = 0; i < n && v + 2 <= MAX_VERTS; i++) {
        const a0 = Math.PI / 2 - i * da;
        const a1 = a0 - da;
        const j = v * 3;
        this.positions[j] = cx + Math.cos(a0) * radius;
        this.positions[j + 1] = cy + Math.sin(a0) * radius;
        this.positions[j + 2] = 0.088;
        this.positions[j + 3] = cx + Math.cos(a1) * radius;
        this.positions[j + 4] = cy + Math.sin(a1) * radius;
        this.positions[j + 5] = 0.088;
        this.colors[j] = this.tmpColor.r;
        this.colors[j + 1] = this.tmpColor.g;
        this.colors[j + 2] = this.tmpColor.b;
        this.colors[j + 3] = this.tmpColor.r;
        this.colors[j + 4] = this.tmpColor.g;
        this.colors[j + 5] = this.tmpColor.b;
        v += 2;
      }
    }
    this.geometry.setDrawRange(0, v);
    this.geometry.attributes.position.needsUpdate = true;
    this.geometry.attributes.color.needsUpdate = true;
  }

  dispose(): void {
    this.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
